import { HistoricalRoadReference } from '@/domain/HistoricalRoadReference';

const CATEGORY_BY_ENUM_ID = new Map<number, string>([
    [5492, 'E'],
    [5493, 'R'],
    [5494, 'F'],
    [5495, 'K'],
    [5496, 'P'],
    [5497, 'S'],
]);

const STATUS_BY_ENUM_ID = new Map<number, string>([
    [5499, 'V'],
    [5505, 'W'],
    [5502, 'T'],
    [5504, 'S'],
    [12159, 'G'],
    [12983, 'U'],
    [13707, 'B'],
    [5501, 'M'],
    [5500, 'X'],
    [7041, 'A'],
    [12160, 'H'],
    [7042, 'P'],
    [7046, 'E'],
    [12986, 'Q'],
]);

interface HistoricVegobjektResponse {
    objekter?: HistoricVegobjekt[];
}

interface HistoricVegobjekt {
    metadata?: {
        startdato?: string;
        sluttdato?: string;
    };
    egenskaper?: HistoricEgenskap[];
    lokasjon?: {
        stedfestinger?: HistoricStedfesting[];
        geometri?: {
            wkt?: string;
        };
        vegsystemreferanser?: Array<{
            kortform?: string;
        }>;
    };
}

interface HistoricEgenskap {
    id: number;
    verdi?: string | number;
    enum_id?: number;
}

interface HistoricStedfesting {
    veglenkesekvensid?: number;
    startposisjon?: number;
    sluttposisjon?: number;
}

export function transformHistoricalRoadReferences(
    data: HistoricVegobjektResponse,
    targetPosition: number
): HistoricalRoadReference[] {
    const references = (data.objekter ?? [])
        .map(objekt => transformHistoricalRoadReference(objekt, targetPosition))
        .filter((reference): reference is HistoricalRoadReference => reference !== null);

    return mergeDuplicateReferences(references).sort(compareNewestFirst);
}

export function transformHistoricalRoadReferencesFromMeter(
    data: HistoricVegobjektResponse,
    targetMeter: number
): HistoricalRoadReference[] {
    const references = (data.objekter ?? [])
        .map(objekt => {
            const targetPosition = calculatePosition(objekt, targetMeter);
            return targetPosition == null
                ? null
                : transformHistoricalRoadReference(objekt, targetPosition);
        })
        .filter((reference): reference is HistoricalRoadReference => reference !== null);

    return mergeDuplicateReferences(references).sort(compareNewestFirst);
}

function transformHistoricalRoadReference(
    objekt: HistoricVegobjekt,
    targetPosition: number
): HistoricalRoadReference | null {
    const egenskaper = new Map(
        (objekt.egenskaper ?? []).map(egenskap => [egenskap.id, egenskap])
    );
    const stedfesting = objekt.lokasjon?.stedfestinger?.[0];

    if (!stedfesting?.veglenkesekvensid) return null;

    const roadReference = createRoadReference(egenskaper, stedfesting, targetPosition);
    if (!roadReference) return null;

    const roadLinkPosition = `${formatPosition(targetPosition)}@${stedfesting.veglenkesekvensid}`;

    return new HistoricalRoadReference(
        roadReference,
        objekt.metadata?.startdato ?? '',
        objekt.metadata?.sluttdato ?? null,
        roadLinkPosition,
        objekt.lokasjon?.geometri?.wkt ?? null,
        objekt.lokasjon?.vegsystemreferanser?.[0]?.kortform ?? null
    );
}

function createRoadReference(
    egenskaper: Map<number, HistoricEgenskap>,
    stedfesting: HistoricStedfesting,
    targetPosition: number
): string | null {
    const category = CATEGORY_BY_ENUM_ID.get(Number(egenskaper.get(4566)?.enum_id));
    const status = STATUS_BY_ENUM_ID.get(Number(egenskaper.get(4567)?.enum_id));
    const roadNumber = egenskaper.get(4568)?.verdi;
    const hp = egenskaper.get(4569)?.verdi;
    const county = egenskaper.get(4591)?.verdi;
    const municipality = egenskaper.get(4592)?.verdi;

    if (
        !category ||
        !status ||
        roadNumber == null ||
        hp == null ||
        county == null ||
        municipality == null
    ) {
        return null;
    }

    const meter = calculateMeter(egenskaper, stedfesting, targetPosition);
    if (meter == null) return null;

    return `${padNumber(Number(county), 2)}${padNumber(
        Number(municipality),
        2
    )} ${category}${status}${roadNumber} hp${hp} m${meter}`;
}

function calculateMeter(
    egenskaper: Map<number, HistoricEgenskap>,
    stedfesting: HistoricStedfesting,
    targetPosition: number
): number | null {
    const fromMeter = Number(egenskaper.get(4571)?.verdi);
    const toMeter = Number(egenskaper.get(4572)?.verdi);
    const startPosition = Number(stedfesting.startposisjon);
    const endPosition = Number(stedfesting.sluttposisjon);

    if (
        ![fromMeter, toMeter, startPosition, endPosition, targetPosition].every(
            Number.isFinite
        )
    ) {
        return null;
    }

    if (startPosition === endPosition) return Math.round(fromMeter);

    const share = (targetPosition - startPosition) / (endPosition - startPosition);
    return Math.round(fromMeter + share * (toMeter - fromMeter));
}

function calculatePosition(
    objekt: HistoricVegobjekt,
    targetMeter: number
): number | null {
    const egenskaper = new Map(
        (objekt.egenskaper ?? []).map(egenskap => [egenskap.id, egenskap])
    );
    const stedfesting = objekt.lokasjon?.stedfestinger?.[0];
    if (!stedfesting) return null;

    const fromMeter = Number(egenskaper.get(4571)?.verdi);
    const toMeter = Number(egenskaper.get(4572)?.verdi);
    const startPosition = Number(stedfesting.startposisjon);
    const endPosition = Number(stedfesting.sluttposisjon);

    if (
        ![fromMeter, toMeter, startPosition, endPosition, targetMeter].every(
            Number.isFinite
        )
    ) {
        return null;
    }

    if (fromMeter === toMeter) return startPosition;

    const share = (targetMeter - fromMeter) / (toMeter - fromMeter);
    return startPosition + share * (endPosition - startPosition);
}

function mergeDuplicateReferences(
    references: HistoricalRoadReference[]
): HistoricalRoadReference[] {
    const mergedByKey = new Map<string, HistoricalRoadReference>();

    references.forEach(reference => {
        const key = `${reference.roadReference}|${reference.roadLinkPosition}`;
        const existing = mergedByKey.get(key);
        if (!existing) {
            mergedByKey.set(key, reference);
            return;
        }

        mergedByKey.set(
            key,
            new HistoricalRoadReference(
                reference.roadReference,
                minDate(existing.fromDate, reference.fromDate),
                maxDate(existing.toDate, reference.toDate),
                reference.roadLinkPosition,
                existing.coordinate ?? reference.coordinate,
                existing.currentVegsystemreferanse ?? reference.currentVegsystemreferanse
            )
        );
    });

    return Array.from(mergedByKey.values());
}

function compareNewestFirst(
    a: HistoricalRoadReference,
    b: HistoricalRoadReference
): number {
    return dateValue(b.toDate ?? b.fromDate) - dateValue(a.toDate ?? a.fromDate);
}

function minDate(first: string, second: string): string {
    if (!first) return second;
    if (!second) return first;
    return dateValue(first) <= dateValue(second) ? first : second;
}

function maxDate(first: string | null, second: string | null): string | null {
    if (!first || !second) return null;
    return dateValue(first) >= dateValue(second) ? first : second;
}

function dateValue(date: string): number {
    return new Date(date).getTime();
}

function padNumber(value: number, length: number): string {
    return value.toString().padStart(length, '0');
}

function formatPosition(position: number): string {
    return Number(position.toFixed(8)).toString();
}
